import { ApiKey, ApiKeyType, ApiMeta, ApiType, TypedApiConfig } from '@/api__';

/**
 * Given an api class, return a key that can look up api metadata from ApiMeta
 */
export function toApiKey(apiType?: ApiType) {
  if (!apiType) {
    return undefined;
  }

  const meta = Object.values(ApiMeta).find((item) => {
    return item.api === apiType;
  });
  const key = meta?.key as ApiKeyType | undefined;
  return key;
}

/**
 * Given an api class, return a TypedApiConfig object that can be used to create an ApiClient.
 * It accepts an optional headers, and the config's Accept header is set with the given API's version.
 * If a token is specified, it's set in the config's Authorization Bearer header.
 */
export function newApiConfig<T>(
  apiType: TypedApiConfig<T>['apiType'],
  headers: Record<string, string> = {},
  token?: string,
): TypedApiConfig<T> {
  const key = toApiKey(apiType);

  const accept = key && {
    Accept: `application/vnd.smartthings+json;v=${ApiMeta[key].version}`,
  };

  const authorization = token && { Authorization: `Bearer ${token}` };

  const baseApiParams: TypedApiConfig<T>['baseApiParams'] = {
    headers: { ...headers, ...accept, ...authorization },
  };

  return { baseApiParams, apiType };
}

/**
 * Given an api class, return a new instance of an api client, which is typed
 * to the specific API. The api client instance also has the api's key that can
 * be used to look up api meta data from ApiMeta.
 *
 * This function optionally takes an ApiConfig as parameter to allow
 * customizing the base behavior.  If it's not provided, a default ApiConfig
 * is created based on the specified api class.
 */
export function getApiClient<T>(apiConfig: TypedApiConfig<T>): T & ApiKey {
  const ApiClass = apiConfig.apiType;

  const apiClient = new ApiClass(apiConfig) as T & ApiKey;

  apiClient.apiKey = toApiKey(apiConfig.apiType);
  return apiClient;
}
